import { type FC, useState } from 'react';

import { useContent, useImageMapper } from '@eneco/cms';
import { type Image as CmsImage } from '@eneco/cms-types';
import type { FeaturePreviewContent } from '@eneco/cms-types/generated/FeaturePreview';
import { FeaturePreviewItem } from '@eneco/custom-components/FeaturePreviewItem';
import { Accordion, Grid, Image, Stack, Text, TileBar, TransitionOpacity } from '@eneco/sparky';
import { useMediaQuery } from '@eneco/sparky/hooks';
import { ChevronRightIcon } from '@eneco/sparky/icons';
import { useDebounce } from 'react-use';

import { getIllustration } from './illustrations';

const FeaturePreviewImage: FC<{ image: CmsImage | undefined }> = ({ image }) => {
  if (!image?.src) return null;

  return (
    <Image
      alt={image.alt ?? ''}
      display="block"
      width="100%"
      src={image.src}
      sources={image.sources}
      aspectRatio={image.aspectRatio}
    />
  );
};

const IconIllustration: FC<{ illustration?: string }> = ({ illustration }) => {
  const Illustration = getIllustration(illustration);
  return Illustration ? <Illustration size="small" /> : null;
};

// Sitecore items retain `id`; Sanity list items retain `_key` in normalized content.
const getItemId = (item: NonNullable<FeaturePreviewContent['items']>[number], index: number): string => {
  if ('id' in item && typeof item.id === 'string') return item.id;
  if ('_key' in item && typeof item._key === 'string') return item._key;
  return String(index);
};

const FeaturePreview: FC = () => {
  const { items = [] } = useContent<FeaturePreviewContent>();
  const mapImage = useImageMapper();
  const [selectedItemId, setSelectedItemId] = useState(() => (items[0] ? getItemId(items[0], 0) : undefined));
  const [currentImage, setCurrentImage] = useState<CmsImage | undefined>(() => mapImage(items[0]?.image));
  const [newImage, setNewImage] = useState<CmsImage | undefined>();
  const [isTransitioning, setTransitioning] = useState(false);
  const isDesktop = useMediaQuery('md');

  useDebounce(
    () => {
      if (newImage) {
        setCurrentImage(newImage);
        setTransitioning(false);
      }
    },
    300,
    [selectedItemId],
  );

  return isDesktop ? (
    <Grid gridTemplateColumns="2fr 3fr" gap="4">
      <Grid.Item>
        <TileBar direction="column">
          {items.map((item, index) => {
            const itemId = getItemId(item, index);
            return (
              <FeaturePreviewItem
                key={itemId}
                onClick={() => {
                  if (selectedItemId === itemId) return;
                  setSelectedItemId(itemId);
                  setNewImage(mapImage(item.image));
                  setTransitioning(true);
                }}
                isSelected={itemId === selectedItemId}>
                <Stack direction="row" alignX="justify" alignY="center">
                  <Stack gap="8" direction="row" alignY="center">
                    <IconIllustration illustration={item.illustration} />
                    {item.title && <Text weight="bold">{item.title}</Text>}
                  </Stack>
                  <ChevronRightIcon />
                </Stack>
              </FeaturePreviewItem>
            );
          })}
        </TileBar>
      </Grid.Item>
      {isTransitioning && newImage ? (
        <>
          <Grid.Item gridRow={1} gridColumn={2}>
            <TransitionOpacity in={!isTransitioning}>
              <FeaturePreviewImage image={currentImage} />
            </TransitionOpacity>
          </Grid.Item>
          <Grid.Item gridRow={1} gridColumn={2}>
            <TransitionOpacity in={isTransitioning}>
              <FeaturePreviewImage image={newImage} />
            </TransitionOpacity>
          </Grid.Item>
        </>
      ) : (
        <Grid.Item gridRow={1} gridColumn={2}>
          <FeaturePreviewImage image={currentImage} />
        </Grid.Item>
      )}
    </Grid>
  ) : (
    <Accordion>
      {items.map((item, index) => (
        <Accordion.Item
          leftIcon={<IconIllustration illustration={item.illustration} />}
          noContentPadding
          hasChevronBg={false}
          key={getItemId(item, index)}
          heading={item.title ?? ''}>
          <FeaturePreviewImage image={mapImage(item.image)} />
        </Accordion.Item>
      ))}
    </Accordion>
  );
};

export default FeaturePreview;
